import { z } from 'zod';

import { PHASE1_PRODUCT_ID } from '../../features';
import { Currency } from '../billing';
import {
  ApiOk,
  canonicalVoucher,
  IsoDate,
  Page,
  PageQuery,
  Uid,
  Ulid,
  VOUCHER_CODE_REGEX,
} from '../primitives';
import { RowError, SoftDelete, TextFilter, Timestamps } from './common';

export const GrantMatchType = z.enum(['device', 'email', 'voucher']);
export type GrantMatchType = z.infer<typeof GrantMatchType>;

export const GrantStatus = z.enum(['pending', 'redeemed', 'expired', 'revoked']);
export type GrantStatus = z.infer<typeof GrantStatus>;

export const GrantTxType = z.enum(['retail', 'bundle', 'gift', 'promo', 'manual']);
export type GrantTxType = z.infer<typeof GrantTxType>;

export const GrantTrigger = z.enum(['signup', 'login', 'voucher_input', 'device_precheck']);
export type GrantTrigger = z.infer<typeof GrantTrigger>;

/**
 * A `grant_redemptions` row, joined with the grant it belongs to so both the user detail
 * (CMS-10 "Canjes de grants") and the grant detail can render a line without a second call.
 */
export const GrantRedemptionDTO = z.object({
  id: Ulid,
  grantId: Ulid,
  userId: Uid,
  subscriptionId: Ulid,
  redeemedAt: IsoDate,
  trigger: GrantTrigger,
  /** Joined from `grants` / `users` (null when the row was written before the join existed). */
  matchType: GrantMatchType.nullable(),
  matchValue: z.string().nullable(),
  productId: z.string().nullable(),
  userEmail: z.string().nullable(),
});
export type GrantRedemptionDTO = z.infer<typeof GrantRedemptionDTO>;

/** A `grants` row (docs/02 §1 "pending subscriptions"). */
export const GrantDTO = z
  .object({
    id: Ulid,
    matchType: GrantMatchType,
    matchValue: z.string(),
    productId: z.string(),
    durationDays: z.number().int().positive().nullable(),
    startsOnRedeem: z.boolean(),
    priceAmountMinor: z.number().int().nonnegative().nullable(),
    currency: Currency.nullable(),
    txType: GrantTxType,
    externalRef: z.string().nullable(),
    maxRedemptions: z.number().int().min(1),
    redemptions: z.number().int().nonnegative(),
    validUntil: IsoDate.nullable(),
    status: GrantStatus,
    notes: z.string().nullable(),
    createdBy: Uid,
    /** Email of `created_by`, joined for the "creado por" line of CMS-09/CMS-11. */
    createdByEmail: z.string().nullable(),
  })
  .merge(Timestamps)
  .merge(SoftDelete);
export type GrantDTO = z.infer<typeof GrantDTO>;

/** `GET /cms/grants?type=&status=&q=` */
export const CmsGrantsQuery = PageQuery.extend({
  type: GrantMatchType.optional(),
  status: GrantStatus.optional(),
  productId: z.string().optional(),
  q: TextFilter.optional(),
});
export type CmsGrantsQuery = z.infer<typeof CmsGrantsQuery>;
export type CmsGrantsQueryInput = z.input<typeof CmsGrantsQuery>;

/** Rows per `match_type`, so CMS-09 can label its tabs without three extra calls. */
export const GrantCounts = z.object({
  device: z.number().int().nonnegative(),
  email: z.number().int().nonnegative(),
  voucher: z.number().int().nonnegative(),
});
export type GrantCounts = z.infer<typeof GrantCounts>;

export const CmsGrantsResponse = Page(GrantDTO).extend({ counts: GrantCounts });
export type CmsGrantsResponse = z.infer<typeof CmsGrantsResponse>;

const GrantFields = z.object({
  /** Phase 1: always `plan_full` (docs/07 §1); the CMS never sends anything else. */
  productId: z.string().min(1).default(PHASE1_PRODUCT_ID),
  durationDays: z.number().int().positive().nullable().default(null),
  startsOnRedeem: z.boolean().default(true),
  priceAmountMinor: z.number().int().nonnegative().nullable().default(null),
  currency: Currency.nullable().default(null),
  txType: GrantTxType,
  externalRef: z.string().max(200).nullable().default(null),
  maxRedemptions: z.number().int().min(1).default(1),
  validUntil: IsoDate.nullable().default(null),
  notes: z.string().max(2000).nullable().default(null),
});

/**
 * Shape a `matchValue` must have for the grant to be able to match anything at all.
 *
 * Same rule the CSV importer already applied per row (`invalid_email` / `invalid_voucher`); the
 * single-grant form and `POST /cms/grants` used to accept literally any string, so `not-an-email`
 * created a grant nobody could ever redeem (QA 2026-09-15 UX-205). A device id has no shape.
 */
export const GRANT_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function grantMatchValueIssue(
  matchType: GrantMatchType,
  value: string,
): 'invalid_email' | 'invalid_voucher' | null {
  const trimmed = value.trim();
  // An empty value is already rejected by `min(1)`; do not report two issues for it.
  if (trimmed === '') return null;
  if (matchType === 'email') return GRANT_EMAIL_REGEX.test(trimmed) ? null : 'invalid_email';
  if (matchType === 'voucher') {
    return VOUCHER_CODE_REGEX.test(canonicalVoucher(trimmed)) ? null : 'invalid_voucher';
  }
  return null;
}

/** Single grant. `matchValue` is a device id, an email (lower-cased) or a voucher code. */
export const CmsGrantCreateSingle = GrantFields.extend({
  mode: z.literal('single'),
  matchType: GrantMatchType,
  matchValue: z.string().trim().min(1).max(254),
});
export type CmsGrantCreateSingle = z.infer<typeof CmsGrantCreateSingle>;

/** Bulk CSV (header in `GRANT_CSV_COLUMNS`). */
export const CmsGrantCreateBulk = z.object({
  mode: z.literal('bulk'),
  csv: z
    .string()
    .min(1)
    .max(5 * 1024 * 1024),
  dryRun: z.boolean().default(false),
});
export type CmsGrantCreateBulk = z.infer<typeof CmsGrantCreateBulk>;

/** Voucher code prefix (`AKP` → `AKP-XXXX-XXXX`, docs/07 §3). */
export const VoucherPrefix = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{2,5}$/);

/** Voucher generator (docs/06 §2.8): N unique `AKP-XXXX-XXXX` codes. */
export const CmsGrantCreateVouchers = GrantFields.extend({
  mode: z.literal('vouchers'),
  count: z.number().int().min(1).max(1000),
  prefix: VoucherPrefix.default('AKP'),
});
export type CmsGrantCreateVouchers = z.infer<typeof CmsGrantCreateVouchers>;

/**
 * `POST /cms/grants`
 *
 * The `matchValue` shape check rides on the union rather than on `CmsGrantCreateSingle`, because
 * `z.discriminatedUnion` only accepts plain objects as options (a refined one is a `ZodEffects`).
 */
export const CmsGrantCreateBody = z
  .discriminatedUnion('mode', [CmsGrantCreateSingle, CmsGrantCreateBulk, CmsGrantCreateVouchers])
  .superRefine((body, ctx) => {
    if (body.mode !== 'single') return;
    const issue = grantMatchValueIssue(body.matchType, body.matchValue);
    if (issue) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['matchValue'], message: issue });
    }
  });
export type CmsGrantCreateBody = z.infer<typeof CmsGrantCreateBody>;
export type CmsGrantCreateBodyInput = z.input<typeof CmsGrantCreateBody>;

export const GrantCsvRowStatus = z.enum(['ok', 'warning', 'error']);
export type GrantCsvRowStatus = z.infer<typeof GrantCsvRowStatus>;

/**
 * Machine-readable outcome of one CSV line; the console maps it to
 * `cms:grants.csv.issue.<code>` (i18n en+es) so the API never ships display copy.
 */
export const GrantCsvIssue = z.enum([
  'unknown_match_type',
  'missing_match_value',
  'invalid_email',
  'invalid_voucher',
  'invalid_duration',
  'invalid_tx_type',
  'invalid_date',
  'invalid_columns',
  'duplicate_in_file',
  'already_exists',
  'email_lowercased',
]);
export type GrantCsvIssue = z.infer<typeof GrantCsvIssue>;

/** One line of the dry-run report of CMS-09 ("fila · tipo · match value · días · resultado"). */
export const GrantCsvRowDTO = z.object({
  /** 1-based line number in the file, header included (so the first data row is 2). */
  row: z.number().int().min(1),
  matchType: GrantMatchType.nullable(),
  matchValue: z.string().nullable(),
  durationDays: z.number().int().nullable(),
  status: GrantCsvRowStatus,
  issue: GrantCsvIssue.nullable(),
});
export type GrantCsvRowDTO = z.infer<typeof GrantCsvRowDTO>;

export const CmsGrantBulkResult = z.object({
  mode: z.literal('bulk'),
  dryRun: z.boolean(),
  created: z.number().int().nonnegative(),
  /** Rows that would be written (`ok` + `warning`). */
  valid: z.number().int().nonnegative(),
  warnings: z.number().int().nonnegative(),
  errors: z.array(RowError),
  rows: z.array(GrantCsvRowDTO),
  grants: z.array(GrantDTO),
});
export type CmsGrantBulkResult = z.infer<typeof CmsGrantBulkResult>;

export const CmsGrantCreateResponse = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('single'), grant: GrantDTO }),
  CmsGrantBulkResult,
  z.object({ mode: z.literal('vouchers'), grants: z.array(GrantDTO) }),
]);
export type CmsGrantCreateResponse = z.infer<typeof CmsGrantCreateResponse>;

/** `PATCH /cms/grants/:id` */
export const CmsGrantPatchBody = GrantFields.partial()
  .extend({ matchValue: z.string().trim().min(1).max(254).optional() })
  .refine((body) => Object.keys(body).length > 0, { message: 'At least one field is required' });
export type CmsGrantPatchBody = z.infer<typeof CmsGrantPatchBody>;

export const CmsGrantResponse = z.object({ grant: GrantDTO });
export type CmsGrantResponse = z.infer<typeof CmsGrantResponse>;

/** `GET /cms/grants/:id` */
export const CmsGrantDetailResponse = z.object({
  grant: GrantDTO,
  redemptions: z.array(GrantRedemptionDTO),
});
export type CmsGrantDetailResponse = z.infer<typeof CmsGrantDetailResponse>;

/** `DELETE /cms/grants/:id` (logical) */
export const CmsGrantDeleteResponse = ApiOk;
export type CmsGrantDeleteResponse = z.infer<typeof CmsGrantDeleteResponse>;

/** `POST /cms/grants/:id/revoke` */
export const CmsGrantRevokeBody = z.object({ reason: z.string().max(500).optional() });
export type CmsGrantRevokeBody = z.infer<typeof CmsGrantRevokeBody>;
export const CmsGrantRevokeResponse = CmsGrantResponse;
export type CmsGrantRevokeResponse = z.infer<typeof CmsGrantRevokeResponse>;

/**
 * CSV header expected by the bulk import (docs/06 §2.8, phase-1 shape — Figma CMS-09).
 * `product_id`, `price` and `currency` were dropped with the single-product decision of
 * 2026-09-08 (docs/07 §1); a file that still carries them is accepted and they are ignored.
 */
export const GRANT_CSV_COLUMNS = [
  'match_type',
  'match_value',
  'duration_days',
  'tx_type',
  'external_ref',
  'valid_until',
  'notes',
] as const;

/** The two columns a row cannot omit. */
export const GRANT_CSV_REQUIRED_COLUMNS = ['match_type', 'match_value'] as const;
